import { useState, type FormEvent } from "react";

import { refusalText } from "../../../api/errors";
import { useApiCommand } from "../../../api/hooks";
import type { AdminPersonView } from "../../../api/types";
import { Button } from "../../../ui/Button";
import { Dialog } from "../../../ui/Dialog";
import { TextField } from "../../../ui/TextField";
import { showToast } from "../../../ui/toastStore";
import { checkNewAccount } from "./accountChecks";
import { CheckList } from "./CheckList";
import { usePeopleCache } from "./useAdminPeople";

interface AddAccountDialogProps {
  existing: readonly AdminPersonView[];
  onClose: () => void;
}

/**
 * Add an account: a user name, a name to show and, if the admin wants, a first password. A first password works once,
 * runs out after a set time and must be changed at the first sign-in. How it reaches the person is outside the app.
 * Mount the dialog to open it: it starts empty every time.
 */
export function AddAccountDialog({ existing, onClose }: AddAccountDialogProps) {
  const create = useApiCommand("create_account");
  const cache = usePeopleCache();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [firstPassword, setFirstPassword] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const result = checkNewAccount({ username, displayName, firstPassword }, existing.map((person) => person.username));
  const started = username.length > 0 || displayName.length > 0 || firstPassword.length > 0;

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!result.ok) return;
    setProblem(null);
    try {
      const reply = await create.mutateAsync({
        username: username.trim(),
        display_name: displayName.trim(),
        ...(firstPassword ? { first_password: firstPassword } : {}),
      });
      cache.replace(reply.data);
      setFirstPassword("");
      showToast({
        text: firstPassword
          ? `Account made for ${reply.data.display_name}. Give them the first password; they must change it when they sign in.`
          : `Account made for ${reply.data.display_name}. No first password is set yet: use "Set a first password" when you are ready.`,
      });
      onClose();
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <Dialog
      closeOnBackdrop={false}
      open
      title="Add an account"
      onClose={create.isPending ? () => undefined : onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button variant="primary" busy={create.isPending} disabled={!result.ok} onClick={() => void submit()}>
            Add the account
          </Button>
        </>
      }
    >
      <form id="ra-add-account" className="stack" onSubmit={(event) => void submit(event)} autoComplete="off">
        <TextField label="User name" value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="off" data-autofocus />
        <TextField label="Name shown on screen" value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="off" />
        <TextField
          label="First password (optional)"
          hint="It works once, runs out after a set time (24 hours unless the installation says otherwise) and must be changed at the first sign-in."
          type="password"
          value={firstPassword}
          onChange={(event) => setFirstPassword(event.target.value)}
          autoComplete="new-password"
        />
        <CheckList checks={result.checks} started={started} />
        {problem && (
          <p role="alert" className="form-error">
            {problem}
          </p>
        )}
      </form>
    </Dialog>
  );
}
